import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router';
import { CountryPackFacts } from '../../components/CountryPack.tsx';
import { CountrySelect } from '../../components/CountrySelect.tsx';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { ReceiptPaper } from '../../components/Receipt.tsx';
import { Button, Dialog, Field, Skeleton } from '../../components/ui.tsx';
import { LANGUAGES, availableLanguages, currentLang, t, type MessageKey } from '../../i18n/index.ts';
import { action, download } from '../../lib/api.ts';
import {
  LIMITS,
  sameDetails,
  sampleReceipt,
  serverErrors,
  taxIdLooksRight,
  toForm,
  toPayload,
  validate,
  type BusinessErrors,
  type BusinessField,
  type BusinessForm,
  type PaperWidth,
} from '../../lib/business.ts';
import { checkSoon, useReceiptPrinter } from '../../lib/printing.ts';
import { useSession } from '../../lib/session.tsx';
import { idHint } from '../../lib/country.ts';
import { useTaxRates, type CountryRules } from '../../lib/taxes.ts';
import type { Company } from '../../lib/types.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

type SaveState = 'clean' | 'dirty' | 'invalid' | 'saving' | 'saved' | 'failed' | 'conflict';

const STATUS: Record<SaveState, { icon: IconName; tone: string; key: MessageKey }> = {
  clean: { icon: 'checkCircle', tone: 'neutral', key: 'business.status_clean' },
  dirty: { icon: 'edit', tone: 'warn', key: 'business.status_dirty' },
  invalid: { icon: 'alert', tone: 'danger', key: 'business.status_invalid' },
  saving: { icon: 'circle', tone: 'info', key: 'business.status_saving' },
  saved: { icon: 'checkCircle', tone: 'ok', key: 'business.status_saved' },
  failed: { icon: 'alert', tone: 'danger', key: 'business.status_failed' },
  conflict: { icon: 'alert', tone: 'warn', key: 'business.status_conflict' },
};

/** Where the save stands, in words, an icon and a colour; read out when it changes. */
function SaveStatus({ state }: { state: SaveState }) {
  const s = STATUS[state];
  return (
    <span className={`save-status save-${s.tone}`} role="status" aria-live="polite">
      {state === 'saving' ? <span className="spinner sm" aria-hidden="true" /> : <Icon name={s.icon} size={16} />}
      <span>{t(s.key)}</span>
    </span>
  );
}

function Section({ icon, title, text, children }: { icon: IconName; title: string; text: string; children: ReactNode }) {
  const id = useId();
  return (
    <section className="card biz-section" aria-labelledby={id}>
      <header className="biz-section-head">
        <span className="biz-section-icon" aria-hidden="true">
          <Icon name={icon} size={20} />
        </span>
        <div>
          <h2 id={id}>{title}</h2>
          <p className="muted">{text}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

/** The owner can take all the business's data away at any time, as CSV files in a ZIP. */
function ExportCard() {
  const { busy, run } = useSave();
  return (
    <section className="card export-card" aria-labelledby="export-title">
      <div>
        <h2 id="export-title">
          <Icon name="download" size={18} /> {t('business.export_title')}
        </h2>
        <p className="muted">{t('business.export_body')}</p>
      </div>
      <Button icon="download" busy={busy} onClick={() => void run(() => download('/api/export'), t('business.export_done'))}>
        {t('business.export')}
      </Button>
    </section>
  );
}

export function Business() {
  const { data, reload } = useAction<Company>('company.get');
  const { can } = useSession();
  const { fail, toast } = useUi();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState<BusinessForm | null>(null);
  /** The details as the server last confirmed them. */
  const [saved, setSaved] = useState<BusinessForm | null>(null);
  const [tried, setTried] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<BusinessField, boolean>>>({});
  const [serverErr, setServerErr] = useState<BusinessErrors>({});
  const [phase, setPhase] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  /** Someone else saved other details since this page loaded. */
  const [conflict, setConflict] = useState<BusinessForm | null>(null);
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const [paper, setPaper] = useState<PaperWidth>('80');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [testing, setTesting] = useState(false);
  const receiptPrinter = useReceiptPrinter();

  useEffect(() => {
    if (data && !form) {
      setForm(toForm(data));
      setSaved(toForm(data));
    }
  }, [data, form]);

  const dirty = !!form && !!saved && !sameDetails(form, saved);
  // The country pack of the country in the form: names, checks and formats of its numbers and address.
  const { data: rules } = useAction<CountryRules>('company.country_rules', { country: /^[A-Za-z]{2}$/.test(form?.country ?? '') ? form!.country.toUpperCase() : 'US' });
  const errors = useMemo<BusinessErrors>(() => (form ? validate(form, rules) : {}), [form, rules]);
  const shown = (k: BusinessField): string | null => serverErr[k] ?? ((tried || touched[k]) && errors[k] ? errors[k]! : null);
  const errorCount = Object.keys(errors).length;
  const state: SaveState =
    phase === 'saving'
      ? 'saving'
      : conflict
        ? 'conflict'
        : phase === 'failed' && dirty
          ? 'failed'
          : tried && errorCount && dirty
            ? 'invalid'
            : dirty
              ? 'dirty'
              : phase === 'saved'
                ? 'saved'
                : 'clean';

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // A link elsewhere in the app with unsaved changes asks first, too.
  useEffect(() => {
    if (!dirty) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.origin !== window.location.origin) return;
      if (a.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveTo(a.pathname + a.search + a.hash);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty]);

  // Wait for the print sheet to be on the page, then open the browser's print window.
  useEffect(() => {
    if (!printing) return;
    const id = requestAnimationFrame(() => {
      window.print();
      setPrinting(false);
    });
    return () => cancelAnimationFrame(id);
  }, [printing]);

  const taxes = useTaxRates();

  if (!form || !saved) {
    return (
      <ManageLayout title={t('manage.business')} subtitle={t('business.subtitle')}>
        <div className="biz">
          <Skeleton h={560} r="var(--radius-lg)" />
          <Skeleton h={560} r="var(--radius-lg)" />
        </div>
      </ManageLayout>
    );
  }

  const set = (k: BusinessField) => (e: { target: { value: string } }) => {
    // A new country brings its usual paper (Letter in North America) and its own states; numbers
    // the new country does not use are left for the person to clear or keep.
    const country = k === 'country' && e.target.value !== form.country ? { paper: '', region: '' } : {};
    setForm({ ...form, [k]: e.target.value, ...country });
    if (serverErr[k]) setServerErr({ ...serverErr, [k]: undefined });
  };
  const blur = (k: BusinessField) => () => setTouched((x) => (x[k] ? x : { ...x, [k]: true }));

  /** Saves; true when the server confirmed it. */
  const save = async (): Promise<boolean> => {
    if (phase === 'saving' || !dirty) return !dirty;
    setTried(true);
    if (errorCount) {
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return false;
    }
    setPhase('saving');
    try {
      // Never write over details someone else saved after this page loaded.
      const now = toForm(await action<Company>('company.get', {}));
      if (!sameDetails(now, saved)) {
        setConflict(now);
        setPhase('idle');
        return false;
      }
      const c = await action<Company>('company.update', toPayload(form));
      setForm(toForm(c));
      setSaved(toForm(c));
      setServerErr({});
      setTried(false);
      setTouched({});
      setPhase('saved');
      toast(t('business.saved'));
      return true;
    } catch (err) {
      const byField = serverErrors(err);
      setServerErr(byField);
      setPhase('failed');
      if (!Object.keys(byField).length) fail(err);
      return false;
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void save();
  };

  /** Ctrl+S / ⌘S saves, as in any editor. */
  const onKeyDown = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      void save();
    }
  };

  const useTheirs = () => {
    if (!conflict) return;
    setForm(conflict);
    setSaved(conflict);
    setConflict(null);
    setServerErr({});
    void reload();
  };
  const keepMine = async () => {
    if (!conflict) return;
    // The person has seen the newer details and chose to replace them.
    setSaved(conflict);
    setConflict(null);
    setPhase('saving');
    try {
      const c = await action<Company>('company.update', toPayload(form));
      setForm(toForm(c));
      setSaved(toForm(c));
      setPhase('saved');
      toast(t('business.saved'));
    } catch (err) {
      setServerErr(serverErrors(err));
      setPhase('failed');
      fail(err);
    }
  };

  const testPrinter = async () => {
    const id = receiptPrinter.register?.printer;
    if (!id) return;
    setTesting(true);
    try {
      await action('printing.test', { printer: id });
      toast(t('business.test_sent'));
      checkSoon();
    } catch (err) {
      fail(err);
    } finally {
      setTesting(false);
    }
  };

  const receipt = sampleReceipt(form, undefined, taxes.standard);
  const canTest = !!receiptPrinter.register?.printer && can('printing.manage');
  const footerLen = form.receipt_footer.length;

  return (
    <ManageLayout
      title={t('manage.business')}
      subtitle={t('business.subtitle')}
      actions={
        <>
          <SaveStatus state={state} />
          <Button variant="primary" icon="check" onClick={() => void save()} busy={phase === 'saving'} disabled={!dirty || !!conflict}>
            {t('business.save')}
          </Button>
        </>
      }
    >
      {conflict ? (
        <div className="conn-bar conn-bar-warn biz-conflict" role="alert">
          <Icon name="alert" size={18} />
          <span>{t('business.conflict')}</span>
          <Button size="sm" variant="secondary" onClick={useTheirs}>
            {t('business.conflict_theirs')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void keepMine()}>
            {t('business.conflict_mine')}
          </Button>
        </div>
      ) : null}

      <div className="biz">
        <div className="biz-main">
          <form ref={formRef} className="biz-form" onSubmit={onSubmit} onKeyDown={onKeyDown} noValidate aria-label={t('manage.business')}>
            <Section icon="store" title={t('business.section_info')} text={t('business.section_info_text')}>
              <div className="biz-grid">
                <Field label={t('business.legal_name')} hint={t('business.legal_name_hint')} error={shown('legal_name')} required>
                  <input className="input" value={form.legal_name} maxLength={LIMITS.legal_name} onChange={set('legal_name')} onBlur={blur('legal_name')} autoComplete="organization" />
                </Field>
                <Field label={t('business.trade_name')} hint={t('business.trade_name_hint')} error={shown('trade_name')}>
                  <input className="input" value={form.trade_name} maxLength={LIMITS.trade_name} onChange={set('trade_name')} onBlur={blur('trade_name')} />
                </Field>
                <Field
                  label={rules?.taxId.label ?? t('business.tax_id')}
                  hint={idHint(rules?.taxId)}
                  error={shown('tax_id')}
                  ok={taxIdLooksRight(form, rules) ? t('business.tax_id_ok') : null}
                >
                  <input
                    className="input mono"
                    value={form.tax_id}
                    maxLength={LIMITS.tax_id}
                    autoCapitalize="characters"
                    spellCheck={false}
                    onChange={set('tax_id')}
                    onBlur={blur('tax_id')}
                  />
                </Field>
                {rules?.vatId || form.vat_id ? (
                  <Field label={rules?.vatId?.label ?? t('business.vat_id')} hint={idHint(rules?.vatId)} error={shown('vat_id')}>
                    <input className="input mono" value={form.vat_id} maxLength={LIMITS.vat_id} autoCapitalize="characters" spellCheck={false} onChange={set('vat_id')} onBlur={blur('vat_id')} />
                  </Field>
                ) : null}
                {rules?.regNo || form.reg_no ? (
                  <Field label={rules?.regNo?.label ?? t('business.reg_no')} hint={idHint(rules?.regNo)} error={shown('reg_no')}>
                    <input className="input mono" value={form.reg_no} maxLength={LIMITS.reg_no} spellCheck={false} onChange={set('reg_no')} onBlur={blur('reg_no')} />
                  </Field>
                ) : null}
                {rules?.einvoice.idLabel || form.einvoice_id ? (
                  <Field label={rules?.einvoice.idLabel ?? t('business.einvoice_id')} hint={t('platform.einvoice_hint')} error={shown('einvoice_id')}>
                    <input className="input mono" value={form.einvoice_id} maxLength={LIMITS.einvoice_id} spellCheck={false} onChange={set('einvoice_id')} onBlur={blur('einvoice_id')} />
                  </Field>
                ) : null}
                <Field label={t('business.country')} hint={t('business.country_hint')} error={shown('country')} required>
                  <CountrySelect value={form.country} onChange={(v) => set('country')({ target: { value: v } })} onBlur={blur('country')} />
                </Field>
              </div>
              {rules && rules.code === form.country.toUpperCase() ? (
                <details className="country-pack-box">
                  <summary>{t('country.pack_title', { country: rules.name })}</summary>
                  <p className="muted small">{t('country.pack_text')}</p>
                  <CountryPackFacts rules={rules} />
                </details>
              ) : null}
              {can('company.manage') ? (
                <p className="biz-tax-link muted">
                  <Icon name="percent" size={16} />
                  <span>{t('business.tax_rates_link')}</span>
                  <Link to="/manage/taxes">{t('manage.taxes')}</Link>
                </p>
              ) : null}
            </Section>

            <Section icon="pin" title={t('business.section_contact')} text={t('business.section_contact_text')}>
              <div className="biz-grid">
                <Field label={t('business.address')} error={shown('address')}>
                  <input className="input" value={form.address} maxLength={LIMITS.address} onChange={set('address')} autoComplete="street-address" />
                </Field>
                <Field label={t('business.city')} error={shown('city')}>
                  <input className="input" value={form.city} maxLength={LIMITS.city} onChange={set('city')} autoComplete="address-level2" />
                </Field>
                <Field label={rules?.address.postcode?.label ?? t('business.postcode')} hint={rules?.address.postcode?.example ? t('business.tax_id_example', { example: rules.address.postcode.example }) : undefined} error={shown('postcode')}>
                  <input className="input" value={form.postcode} maxLength={LIMITS.postcode} autoCapitalize="characters" onChange={set('postcode')} onBlur={blur('postcode')} autoComplete="postal-code" />
                </Field>
                {rules?.regions ? (
                  <Field label={rules.address.regionLabel ?? t('business.region')} hint={t('business.region_hint')} error={shown('region')}>
                    <select className="input" value={form.region} onChange={set('region')} autoComplete="address-level1">
                      <option value="">{t('business.region_choose', { name: (rules.address.regionLabel ?? t('business.region')).toLowerCase() })}</option>
                      {rules.regions.map((r) => (
                        <option key={r.code} value={r.code}>
                          {r.name} ({r.code})
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : rules?.address.regionLabel ? (
                  <Field label={rules.address.regionLabel} error={shown('region')}>
                    <input className="input" value={form.region} maxLength={LIMITS.region} onChange={set('region')} autoComplete="address-level1" />
                  </Field>
                ) : null}
                <Field label={t('business.phone')} error={shown('phone')}>
                  <input className="input" type="tel" value={form.phone} maxLength={LIMITS.phone} onChange={set('phone')} autoComplete="tel" />
                </Field>
                <Field label={t('business.email')} hint={t('business.email_hint')} error={shown('email')}>
                  <input className="input" type="email" value={form.email} maxLength={LIMITS.email} onChange={set('email')} onBlur={blur('email')} autoComplete="email" />
                </Field>
                <Field label={t('business.website')} hint={t('business.website_hint')} error={shown('website')}>
                  <input className="input" type="url" inputMode="url" value={form.website} maxLength={LIMITS.website} onChange={set('website')} autoComplete="url" spellCheck={false} />
                </Field>
              </div>
            </Section>

            <Section icon="receipt" title={t('business.section_receipt')} text={t('business.section_receipt_text')}>
              <div className="biz-grid biz-grid-receipt">
                <Field
                  label={t('business.footer')}
                  hint={t('business.footer_hint')}
                  error={shown('receipt_footer')}
                  counter={`${footerLen}/${LIMITS.receipt_footer}`}
                >
                  <textarea className="input textarea" rows={3} value={form.receipt_footer} maxLength={LIMITS.receipt_footer} onChange={set('receipt_footer')} />
                </Field>
                <Field label={t('business.language')} hint={t('business.language_hint')} error={shown('language')}>
                  <select className="input" value={form.language} onChange={set('language')}>
                    {LANGUAGES.filter((l) => l.code === form.language || availableLanguages().some((a) => a.code === l.code)).map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t('business.paper')} hint={t('business.paper_hint')}>
                  <select className="input" value={form.paper || rules?.paper || 'A4'} onChange={set('paper')}>
                    <option value="A4">{t('business.paper_a4')}</option>
                    <option value="Letter">{t('business.paper_letter')}</option>
                  </select>
                </Field>
              </div>
            </Section>
          </form>
          {can('company.export') ? <ExportCard /> : null}
        </div>

        <aside className={`card biz-preview${previewOpen ? ' open' : ''}`} aria-labelledby="biz-preview-title">
          <header className="biz-preview-head">
            <span className="biz-section-icon" aria-hidden="true">
              <Icon name="eye" size={20} />
            </span>
            <div className="biz-preview-titles">
              <h2 id="biz-preview-title">{t('business.preview')}</h2>
              <p className="muted">{t('business.preview_text')}</p>
            </div>
            <button type="button" className="btn btn-secondary btn-sm biz-preview-toggle" aria-expanded={previewOpen} onClick={() => setPreviewOpen((o) => !o)}>
              <Icon name={previewOpen ? 'eyeOff' : 'eye'} size={16} />
              <span className="btn-label">{previewOpen ? t('business.preview_hide') : t('business.preview_show')}</span>
            </button>
          </header>
          <div className="biz-preview-body">
            <div className="biz-preview-tools">
              <div className="segmented subtle" role="radiogroup" aria-label={t('business.paper_width')}>
                {(['80', '58'] as const).map((w) => (
                  <button key={w} type="button" role="radio" aria-checked={paper === w} className={paper === w ? 'active' : ''} onClick={() => setPaper(w)}>
                    {t(w === '58' ? 'printers.paper_58' : 'printers.paper_80')}
                  </button>
                ))}
              </div>
              <Button variant="secondary" size="sm" icon="printer" onClick={() => setPrinting(true)}>
                {t('business.print_sample')}
              </Button>
              {canTest ? (
                <Button variant="secondary" size="sm" icon="send" onClick={() => void testPrinter()} busy={testing}>
                  {t('business.test_printer')}
                </Button>
              ) : null}
            </div>
            {/* the paper scrolls inside its stage: a named stop in the tab order, so a keyboard can scroll it */}
            <div className="biz-paper-stage" role="region" tabIndex={0} aria-label={t('business.preview')}>
              <ReceiptPaper r={receipt} sample narrow={paper === '58'} />
            </div>
            <p className="biz-note">
              <Icon name="alert" size={16} />
              <span>{t('business.preview_note')}</span>
            </p>
          </div>
        </aside>
      </div>

      {dirty || phase === 'saving' ? (
        <div className="biz-savebar">
          <SaveStatus state={state} />
          <Button variant="primary" icon="check" onClick={() => void save()} busy={phase === 'saving'} disabled={!dirty || !!conflict}>
            {t('business.save')}
          </Button>
        </div>
      ) : null}

      {leaveTo ? (
        <Dialog
          title={t('business.leave_title')}
          onClose={() => setLeaveTo(null)}
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setLeaveTo(null)}>
                {t('business.leave_stay')}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const to = leaveTo;
                  setLeaveTo(null);
                  setSaved(form);
                  navigate(to);
                }}
              >
                {t('business.leave_discard')}
              </Button>
              <Button
                variant="primary"
                icon="check"
                busy={phase === 'saving'}
                onClick={async () => {
                  const to = leaveTo;
                  if (await save()) {
                    setLeaveTo(null);
                    navigate(to);
                  } else setLeaveTo(null);
                }}
              >
                {t('business.leave_save')}
              </Button>
            </>
          }
        >
          <p>{t('business.leave_text')}</p>
        </Dialog>
      ) : null}

      {printing
        ? createPortal(
            <div className="print-sheet">
              <ReceiptPaper r={receipt} sample narrow={paper === '58'} />
            </div>,
            document.body,
          )
        : null}
    </ManageLayout>
  );
}
